import { FormEvent, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import useSubmitLock from 'hooks/use-submit-lock'
import { mountStripePaymentElement } from 'utils/stripe-embedded-checkout'

const STRIPE_PAYMENT_ELEMENT_ID = 'stripe-payment-element'

type StripeUpdateCardDialogProps = {
  open: boolean
  publishableKey?: string | null
  clientSecret?: string | null
  retryInvoice?: boolean
  submitting?: boolean
  errorMessage?: string | null
  onClose: () => void
  onConfirm: (paymentMethodId: string) => Promise<void> | void
}

const StripeUpdateCardDialog = ({
  open,
  publishableKey,
  clientSecret,
  retryInvoice = false,
  submitting = false,
  errorMessage,
  onClose,
  onConfirm,
}: StripeUpdateCardDialogProps) => {
  const [ready, setReady] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const confirmSetupRef = useRef<(() => Promise<string>) | null>(null)
  const { isSubmitting, wrapClick } = useSubmitLock()

  useEffect(() => {
    if (!open || !publishableKey || !clientSecret) {
      return
    }

    let cancelled = false
    let destroy: (() => void) | undefined
    setReady(false)
    setLocalError(null)
    confirmSetupRef.current = null

    const mount = async () => {
      try {
        const payment = await mountStripePaymentElement({
          publishableKey,
          clientSecret,
          targetId: STRIPE_PAYMENT_ELEMENT_ID,
        })
        if (cancelled) {
          payment.destroy()
          return
        }
        confirmSetupRef.current = payment.confirmSetup
        destroy = payment.destroy
        setReady(true)
      } catch (error) {
        if (!cancelled) {
          setLocalError(
            error instanceof Error
              ? error.message
              : 'Unable to load card form. Please try again.'
          )
        }
      }
    }

    void mount()

    return () => {
      cancelled = true
      destroy?.()
      confirmSetupRef.current = null
    }
  }, [clientSecret, open, publishableKey])

  const busy = confirming || submitting || isSubmitting
  const displayError = localError || errorMessage

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!confirmSetupRef.current || busy) return
    setConfirming(true)
    setLocalError(null)
    try {
      const paymentMethodId = await confirmSetupRef.current()
      await onConfirm(paymentMethodId)
    } catch (error) {
      setLocalError(
        error instanceof Error
          ? error.message
          : 'Unable to update card details. Please try again.'
      )
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={wrapClick(handleSubmit)}>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 3, pb: 2, position: 'relative' }}>
            <IconButton
              aria-label="Close"
              onClick={onClose}
              disabled={busy}
              sx={{ position: 'absolute', top: 12, right: 12 }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={700} color="#12355b" pr={5}>
              Update card details
            </Typography>
            <Typography mt={1} color="#6b7280">
              {retryInvoice
                ? 'Enter a new card. We will save it for this subscription and retry this invoice.'
                : 'Enter a new card. We will use it for future subscription payments.'}
            </Typography>
            {displayError && (
              <Typography color="error" fontSize={14} mt={2}>
                {displayError}
              </Typography>
            )}
            <Box sx={{ position: 'relative', minHeight: 180, mt: 2 }}>
              {!ready && !displayError && (
                <Box
                  display="flex"
                  justifyContent="center"
                  py={6}
                  sx={{ position: 'absolute', inset: 0 }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
              <Box id={STRIPE_PAYMENT_ELEMENT_ID} />
            </Box>
          </Box>
          <Divider />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ px: 3, py: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={busy}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!ready || busy}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                minWidth: 120,
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {busy ? <CircularProgress size={18} color="inherit" /> : 'Save Card'}
            </Button>
          </Stack>
        </DialogContent>
      </form>
    </Dialog>
  )
}

export default StripeUpdateCardDialog
